// Borrador PDF - Uri suplementario = slug del tipo de contrato (individual, duo, family, linked)
import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { HiringDetails } from '@/types/hiring';
import type { ContractType } from '@/types/admin';
import { CONTRACT_TYPE_LABELS, CONTRACT_TYPE_PRICING } from '@/types/admin';
import { Button } from '@/components/ui/button';
import { ArrowDownTrayIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { config } from '@/config/constants';

const CONTRACT_TYPES: ContractType[] = ['individual', 'duo', 'family', 'linked'];
const VALID_SLUGS: ContractType[] = CONTRACT_TYPES;

function slugToContractType(slug: string | undefined): ContractType {
  if (slug && VALID_SLUGS.includes(slug as ContractType)) return slug as ContractType;
  return 'individual';
}

const BASE_EXPIRES = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
const BASE_SERVICE = 'Autorización de residencia inicial en España';
const BASE_DESCRIPTION = 'Tramitación de expediente para obtención de autorización de residencia inicial en España';

function buildExampleDetails(type: ContractType, paymentVariant: 'one_time' | 'subscription'): HiringDetails {
  const pricing = CONTRACT_TYPE_PRICING[type];
  const amountCents = (paymentVariant === 'one_time' ? pricing.one_time : pricing.subscription) * 100;
  const base: HiringDetails = {
    id: 999999,
    hiring_code: 'EJEMPLO',
    client_name: 'Juan Pérez García',
    client_email: 'juan.perez@ejemplo.com',
    client_passport: 'X1234567Z',
    client_nie: 'X1234567Z',
    client_address: 'Calle Mayor 123, 1º A',
    client_city: 'Madrid',
    client_province: 'Madrid',
    client_postal_code: '28001',
    service_name: BASE_SERVICE,
    service_description: BASE_DESCRIPTION,
    amount: amountCents,
    currency: 'EUR',
    status: 'pending',
    kyc_status: null,
    expires_at: BASE_EXPIRES,
    short_url: `${config.PUBLIC_APP_URL}/EJEMPLO`,
    grade: 'B',
    payment_type: paymentVariant,
    contract_type: type,
  };

  if (type === 'linked') {
    base.linked_contract_code = 'EJEMPLO2';
    base.linked_partner_name = 'María López Fernández';
    if (paymentVariant === 'subscription') {
      base.first_payment_amount = 3400; // 34€
    }
  }

  if (type === 'duo' || type === 'family') {
    if (paymentVariant === 'subscription') {
      base.first_payment_amount = 6800; // 68€
    }
    base.supplementary_holder = {
      client_name: 'María López Fernández',
      client_email: 'maria.lopez@ejemplo.com',
      client_passport: 'Y7654321M',
      client_nie: 'Y7654321M',
      client_nationality: 'colombiana',
      client_address: 'Calle Mayor 123, 1º A',
      client_city: 'Madrid',
      client_province: 'Madrid',
      client_postal_code: '28001',
    };
  }

  if (type === 'individual' && paymentVariant === 'subscription') {
    base.first_payment_amount = 4800; // 48€
  }

  return base;
}

export function BorradorUriSuplementario() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const contractType = slugToContractType(slug);
  const [paymentVariant, setPaymentVariant] = useState<'one_time' | 'subscription'>('one_time');

  // Redirigir a slug válido si el parámetro no es válido
  useEffect(() => {
    if (slug && !VALID_SLUGS.includes(slug as ContractType)) {
      navigate(`/borrador/individual`, { replace: true });
    }
  }, [slug, navigate]);
  const [contractBlob, setContractBlob] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const pdfUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (pdfUrlRef.current) {
      window.URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = null;
    }
    setPdfUrl(null);

    let cancelled = false;
    const generate = async () => {
      try {
        setLoading(true);
        setError(null);

        const exampleDetails = buildExampleDetails(contractType, paymentVariant);
        const { generateContractPDF } = await import('@/utils/contractPdfGenerator');
        const blob = generateContractPDF(
          exampleDetails,
          {
            paymentIntentId: 'pi_ejemplo_borrador',
            stripeTransactionId: 'ejemplo_borrador_12345',
            paymentDate: new Date().toISOString(),
            paymentMethod: 'Ejemplo de borrador',
            clientSignature: exampleDetails.client_name,
          },
          false,
          undefined
        );

        if (cancelled) return;
        setContractBlob(blob);
        const url = window.URL.createObjectURL(blob);
        pdfUrlRef.current = url;
        setPdfUrl(url);
      } catch (err) {
        if (!cancelled) {
          console.error('Error generando PDF de ejemplo:', err);
          setError('Error al generar el PDF de ejemplo');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    generate();
    return () => {
      cancelled = true;
      if (pdfUrlRef.current) {
        window.URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }
    };
  }, [contractType, paymentVariant]);

  const handleDownload = () => {
    if (contractBlob) {
      const url = window.URL.createObjectURL(contractBlob);
      const link = document.createElement('a');
      link.href = url;
      const slug = CONTRACT_TYPE_LABELS[contractType].replace(/\s*\/\s*/g, '-').replace(/\s+/g, '_').toLowerCase();
      link.download = `borrador_contrato_${slug}_${paymentVariant === 'one_time' ? 'pago_unico' : 'suscripcion'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  };

  const pricing = CONTRACT_TYPE_PRICING[contractType];
  const amountOneTime = pricing.one_time;
  const amountSub = pricing.subscription;

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={() => window.location.reload()} variant="outline">
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto py-4 px-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Borrador por tipo de contrato</h1>
                <p className="text-sm text-gray-600">Uri suplementario = slug del contrato: /borrador/{'{slug}'}</p>
              </div>
              <Button
                onClick={handleDownload}
                disabled={loading || !contractBlob}
                className="flex items-center gap-2 shrink-0"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                Descargar PDF
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-6">
              <div className="flex flex-wrap gap-2">
                <span className="text-sm font-medium text-gray-700">Tipo de contrato:</span>
                {CONTRACT_TYPES.map((t) => (
                  <Button
                    key={t}
                    type="button"
                    variant={contractType === t ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => navigate(`/borrador/${t}`)}
                  >
                    {CONTRACT_TYPE_LABELS[t]}
                  </Button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-sm font-medium text-gray-700">Modalidad:</span>
                <Button
                  type="button"
                  variant={paymentVariant === 'one_time' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPaymentVariant('one_time')}
                >
                  Pago único ({amountOneTime} €)
                </Button>
                <Button
                  type="button"
                  variant={paymentVariant === 'subscription' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPaymentVariant('subscription')}
                >
                  Suscripción ({amountSub} €)
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-600">
              <DocumentTextIcon className="w-4 h-4 shrink-0" />
              <span>
                Modelo actual: <strong>{CONTRACT_TYPE_LABELS[contractType]}</strong>
                {contractType === 'linked' && ' (precio por contrato)'} — {paymentVariant === 'one_time' ? `${amountOneTime} €` : `${amountSub} € (10 cuotas)`}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="py-8">
        <div className="max-w-6xl mx-auto px-4">
          {loading && (
            <div className="flex items-center justify-center py-24">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
                <p className="text-gray-600">Generando borrador...</p>
              </div>
            </div>
          )}
          {!loading && pdfUrl && (
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <iframe
                src={pdfUrl}
                className="w-full h-screen min-h-[800px] border-0"
                title={`Borrador - ${CONTRACT_TYPE_LABELS[contractType]}`}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
